import { useWatch } from "react-hook-form"
import { Lens } from "@hookform/lenses"
import { BetterDocSection } from "@/modules/patients/detail/better-doc/components/better-doc-section"
import { CareProviderContent } from "@/modules/patients/detail/progress-note/point-of-care/components/fields/care-provider-content"
import { DoctorVisitAvoidedContent } from "@/modules/patients/detail/progress-note/point-of-care/components/fields/doctor-visit-avoided-content"
import { ReasonForChoosingType } from "@/modules/patients/detail/progress-note/point-of-care/components/fields/reason-for-choosing-type"
import { SpecialtyContent } from "@/modules/patients/detail/progress-note/point-of-care/components/fields/specialty-content"
import { TimeToTreatContent } from "@/modules/patients/detail/progress-note/point-of-care/components/fields/time-to-treat-content"
import { InsurerNetworkBanner } from "@/modules/patients/detail/progress-note/point-of-care/components/insurer-network-banner"
import { useAppointmentOrRegisteredPatientAddress } from "@/modules/patients/detail/progress-note/point-of-care/hooks/useAppointmentOrRegisteredPatientAddress"
import { PointOfCareFormType } from "@/modules/patients/detail/progress-note/point-of-care/schema"
import type { ContentProps, HasCareProvider } from "@/modules/patients/detail/progress-note/point-of-care/types"
import type { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { MedgateServiceInfo } from "@/modules/patients/services/usePatientByIdQuery"

type SpecialistContentProps = ContentProps & {
  pointOfCareFormLens: Lens<PointOfCareFormType>
  medgateServiceInfo: MedgateServiceInfo
  patientId: string
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
  setCareProvider: (careProvider: string | null) => void
  onSpecialtySelect: (specialtyId: string | undefined) => void
}

export const SpecialistContent = (props: SpecialistContentProps & HasCareProvider) => {
  const {
    pointOfCareFormLens,
    medgateServiceInfo,
    patientId,
    isActual,
    countryCode,
    timeToTreatData,
    episodeOfCareProgressNoteData,
    hasCareProvider,
    setCareProvider,
    onSpecialtySelect,
  } = props
  const specialtyLens = pointOfCareFormLens.focus("specialtyId")
  const timeToTreatLens = pointOfCareFormLens.focus("timeToTreatType")
  const specialty = useWatch(specialtyLens.interop())
  const timeToTreatType = useWatch(timeToTreatLens.interop())

  const specialtyId = specialty?.specialty?.id
  const searchType = specialty ? "SPECIALIST_WITH_SPECIALTY" : "SPECIALIST_WITHOUT_SPECIALTY"
  const patientAddress = useAppointmentOrRegisteredPatientAddress({
    patientId,
    countryCode,
    episodeOfCareProgressNoteData,
  })
  const showInsurerNetwork = countryCode === "CH" && !!specialtyId

  return (
    <div className="col-span-2 gap-4">
      {countryCode === "CH" && medgateServiceInfo.hasInsuranceListReferralToSpecialist && <InsurerNetworkBanner />}
      {isActual && (
        <ReasonForChoosingType
          reasonForChoosingTypeLens={pointOfCareFormLens.focus("reasonForChoosingType")}
          containerClassName="pb-5"
        />
      )}
      {countryCode === "CH" && (
        <SpecialtyContent
          specialtyLens={specialtyLens}
          onSpecialtySelect={(selectedSpecialty) => onSpecialtySelect(selectedSpecialty?.specialty?.id)}
        />
      )}

      <BetterDocSection />
      <div className="flex flex-col gap-4 pt-4 @lg/patient-layout:flex-row">
        {timeToTreatData.length > 0 && (
          <TimeToTreatContent timeToTreatLens={timeToTreatLens} timeToTreatData={timeToTreatData} disabled />
        )}
        <div className="@lg/patient-layout:w-2/3">
          {countryCode === "CH" ? (
            <CareProviderContent
              pointOfCareFormLens={pointOfCareFormLens}
              searchType={searchType}
              specialtyId={specialtyId}
              showInsurerNetwork={showInsurerNetwork}
              patientAddress={patientAddress}
              timeToTreat={timeToTreatType ?? undefined}
              setCareProvider={setCareProvider}
              episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
              hasMPNReferral={medgateServiceInfo.referralToSpecialist === "MPN"}
              hasCareProvider={hasCareProvider}
              useSpecialty
              allowUnknownCareProvider
            />
          ) : (
            <SpecialtyContent specialtyLens={specialtyLens} />
          )}
        </div>
      </div>
      <DoctorVisitAvoidedContent isDoctorVisitAvoidedLens={pointOfCareFormLens.focus("isDoctorVisitAvoided")} />
    </div>
  )
}
